import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './segmented-control.style.js'

import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import type { CSESegmentedControlItem } from 'elements/segmented-control-item/segmented-control-item.js'
import { classMap } from 'lit/directives/class-map.js'


export const selectableFit = ['fit', 'fill'] as const
export type segmentedControlFitType = typeof selectableFit

@customElement('cse-segmented-control')
export class CSESegmentedControl extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property()
  fit: segmentedControlFitType[number] = 'fill'

  @state()
  selectedIndex: number = 0

  @state()
  private indicatorLeft: number = 0

  @state()
  private indicatorWidth: number = 0

  @state()
  private indicatorHeight: number = 0

  @state()
  private animateReady = false

  private resizeObserver?: ResizeObserver

  controlItems!: NodeListOf<CSESegmentedControlItem>
  emit = (value: string) => {
    this.dispatchEvent(new CSCSegmentedControlChangeEvent(this, value))
  }

  removeActiveStates = () => {
    this.controlItems.forEach((controlItem) => { controlItem.removeAttribute('active') })
  }

  activateControlItem = (index = 0) => {
    const selectedControlItem = this.controlItems[index]
    if(!selectedControlItem) return

    this.removeActiveStates()
    this.selectedIndex = index

    selectedControlItem.setAttribute('active', 'active')
    const selectedValue = selectedControlItem.value

    this.determineStates()
    this.updateIndicator()

    this.emit(selectedValue)
  }

  handleClickItem = (controlItem: CSESegmentedControlItem, index: number) => {
    if (!controlItem.disabled) {
      this.activateControlItem(index)
    }
  }

  attachEventListeners = () => {
    this.controlItems.forEach((controlItem, index) => {
      controlItem.addEventListener('click', () => this.handleClickItem(controlItem, index))
    })
  }

  determineStateForControlItem = (controlItem: CSESegmentedControlItem, index: number) => {
    const isRightOfActiveIndex = this.selectedIndex + 1 === index
    const isFirst = index === 0
    const isActive = index === this.selectedIndex

    if (!(isActive || isFirst || isRightOfActiveIndex)) {
      controlItem.setAttribute('behind-active', 'true')
    } else {
      controlItem.removeAttribute('behind-active')
    }
  }

  determineStates = () => {
    this.controlItems.forEach((el, index) => this.determineStateForControlItem(el, index))
  }

  updateIndicator = () => {
    const container = this.renderRoot.querySelector('.cse-segmented-control') as HTMLElement | null
    const activeItem = this.controlItems[this.selectedIndex] as HTMLElement | undefined
    if (!container || !activeItem) return

    const containerRect = container.getBoundingClientRect()
    const activeRect = activeItem.getBoundingClientRect()
    const left = activeRect.left - containerRect.left
    const width = activeRect.width
    const height = activeRect.height

    this.indicatorLeft = left
    this.indicatorWidth = width
    this.indicatorHeight = height
  }

  override connectedCallback() {
    super.connectedCallback()
    this.controlItems = this.querySelectorAll('cse-segmented-control-item')
    this.attachEventListeners()
    this.activateControlItem(0)

    // Observe resize to keep indicator aligned
    this.resizeObserver = new ResizeObserver(() => this.updateIndicator())
    this.resizeObserver.observe(this)
    window.addEventListener('resize', this.updateIndicator)

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.animateReady = true
      })
    })
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.resizeObserver?.disconnect()
    window.removeEventListener('resize', this.updateIndicator)
  }

  override updated(changedProps: Map<string, unknown>): void {
    if (changedProps.has('fit')) {
      requestAnimationFrame(() => this.updateIndicator())
    }
  }

  override render() {
    const indicatorStyle = `left: ${this.indicatorLeft}px; width: ${this.indicatorWidth}px; height: ${this.indicatorHeight}px;`
    return html`<div class="${classMap({ 'cse-segmented-control': true, [`fit-${this.fit}`]: true })}">
      <div class="${classMap({
      'cse-segmented-control__indicator': true,
      'animate': this.animateReady
    })
      }" style=${indicatorStyle}></div>
      <slot @slotchange=${this.updateIndicator}></slot>
    </div>`
  }
}

export class CSCSegmentedControlChangeEvent extends Event {
  static type = 'segmented-control-change' as const

  public segmentedControl: CSESegmentedControl
  public value: string | undefined


  constructor(segmentedControl: CSESegmentedControl, value: string) {
    super(CSCSegmentedControlChangeEvent.type, { bubbles: true, composed: true })

    this.segmentedControl = segmentedControl
    this.value = value
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-segmented-control': CSESegmentedControl
  }
  interface GlobalEventHandlersEventMap {
    [CSCSegmentedControlChangeEvent.type]: CSCSegmentedControlChangeEvent
  }
}
